// web/src/components/Layout/UserProfileDropdown.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { authAPI, mfaAPI, MFAStatusResponse } from '../../services/api';
import MFASetup from '../Auth/MFASetup';
import './UserProfileDropdown.css';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' }
];

interface UserSettings {
  theme: 'light' | 'dark' | 'auto';
  notifications: boolean;
  autoRefresh: boolean;
  refreshInterval: number;
  compactView: boolean;
  scanResultsLimit: number;
}

interface UserProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ isOpen, onClose, anchorRef }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { currentLanguage, changeLanguage } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'appearance' | 'preferences' | 'security'>('profile');
  const [settings, setSettings] = useState<UserSettings>({
    theme: 'light',
    notifications: true,
    autoRefresh: true,
    refreshInterval: 30,
    compactView: false,
    scanResultsLimit: 500,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // MFA state
  const [mfaStatus, setMfaStatus] = useState<MFAStatusResponse | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaMessage, setMfaMessage] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Pushover state
  const [pushoverKey, setPushoverKey] = useState('');
  const [pushoverEnabled, setPushoverEnabled] = useState(false);
  const [pushSaving, setPushSaving] = useState(false);

  // Load settings on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`user_settings_${user?.id}`);
      if (stored) {
        setSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  }, [user?.id]);

  // Load MFA status when security tab is active
  useEffect(() => {
    if (activeTab === 'security' && isOpen) {
      loadMFAStatus();
    }
  }, [activeTab, isOpen]);

  const loadMFAStatus = async () => {
    setMfaLoading(true);
    try {
      const response = await mfaAPI.getStatus();
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to load MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  // Load Pushover settings when preferences tab is active
  useEffect(() => {
    if (activeTab === 'preferences' && isOpen) {
      authAPI.getProfile().then(res => {
        const u = res.data;
        setPushoverEnabled(u.pushover_push_enabled || false);
        setPushoverKey(u.pushover_user_key || '');
      }).catch(() => {});
    }
  }, [activeTab, isOpen]);

  const savePushoverSettings = async () => {
    setPushSaving(true);
    try {
      await authAPI.updateProfile({
        pushover_user_key: pushoverKey,
        pushover_push_enabled: pushoverEnabled,
      });
      setMessage(t('userProfile.pushSettingsSaved', 'Push settings saved'));
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save push settings:', err);
      setMessage(t('userProfile.pushSettingsFailed', 'Failed to save push settings'));
    } finally {
      setPushSaving(false);
    }
  };

  const handleMFASetupComplete = () => {
    setShowMFASetup(false);
    loadMFAStatus();
    setMfaMessage(t('userProfile.mfaEnabledSuccess', 'Two-factor authentication enabled!'));
    setTimeout(() => setMfaMessage(''), 3000);
  };

  const handleChangePassword = async () => {
    setPasswordMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('userProfile.allFieldsRequired', 'All fields are required') });
      return;
    }
    if (newPassword.length < 10) {
      setPasswordMessage({ type: 'error', text: t('userProfile.passwordMinLength', 'New password must be at least 10 characters') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('userProfile.passwordMismatch', 'New passwords do not match') });
      return;
    }

    setPasswordChanging(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordMessage({ type: 'success', text: t('userProfile.passwordChanged', 'Password changed successfully') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordMessage(null), 4000);
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('userProfile.passwordChangeFailed', 'Failed to change password');
      setPasswordMessage({ type: 'error', text: msg });
    } finally {
      setPasswordChanging(false);
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleChange = (key: keyof UserSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = () => {
    setSaving(true);
    setMessage('');

    try {
      localStorage.setItem(`user_settings_${user?.id}`, JSON.stringify(settings));
      setMessage('Settings saved');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-profile-dropdown" ref={dropdownRef}>
      {/* Header */}
      <div className="profile-dropdown-header">
        <div className="profile-avatar">
          <i className="fas fa-user"></i>
        </div>
        <div className="profile-info">
          <span className="profile-name">{user?.username || 'User'}</span>
          <span className="profile-email">{user?.email}</span>
          <span className={`profile-role ${user?.role === 'superuser' ? 'admin' : ''}`}>
            {user?.role === 'superuser' ? t('userProfile.administrator', 'Administrator') : t('userProfile.securityAnalyst', 'Security Analyst')}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <i className="fas fa-user"></i>
          {t('userProfile.profile', 'Profile')}
        </button>
        <button
          className={`profile-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          <i className="fas fa-palette"></i>
          {t('userProfile.appearance', 'Appearance')}
        </button>
        <button
          className={`profile-tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          <i className="fas fa-sliders-h"></i>
          {t('userProfile.preferences', 'Preferences')}
        </button>
        <button
          className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <i className="fas fa-shield-alt"></i>
          {t('userProfile.security', 'Security')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-tab-content">
            <div className="profile-field">
              <label>{t('userProfile.username', 'Username')}</label>
              <div className="profile-value">{user?.username}</div>
            </div>
            <div className="profile-field">
              <label>{t('userProfile.email', 'Email')}</label>
              <div className="profile-value">{user?.email}</div>
            </div>
            <div className="profile-field">
              <label>{t('userProfile.role', 'Role')}</label>
              <div className="profile-value">
                <span className={`role-badge ${user?.role === 'superuser' ? 'admin' : 'user'}`}>
                  {user?.role === 'superuser' ? t('userProfile.administrator', 'Administrator') : t('userProfile.user', 'User')}
                </span>
              </div>
            </div>
            <div className="profile-field">
              <label>{t('userProfile.memberSince', 'Member Since')}</label>
              <div className="profile-value">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>
        )}

        {/* Appearance Tab */}
        {activeTab === 'appearance' && (
          <div className="profile-tab-content">
            <div className="setting-item">
              <div className="setting-label">
                <i className="fas fa-globe"></i>
                <div>
                  <span className="setting-title">{t('userProfile.language', 'Language')}</span>
                  <span className="setting-description">{t('userProfile.languageDescription', 'Choose your display language')}</span>
                </div>
              </div>
              <div className="setting-control">
                <select
                  value={currentLanguage}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="setting-select"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-label">
                <i className="fas fa-moon"></i>
                <div>
                  <span className="setting-title">{t('userProfile.theme', 'Theme')}</span>
                  <span className="setting-description">{t('userProfile.themeDescription', 'Choose your color scheme')}</span>
                </div>
              </div>
              <div className="setting-control">
                <select
                  value={theme}
                  onChange={(e) => {
                    const newTheme = e.target.value as 'light' | 'dark' | 'auto';
                    setTheme(newTheme);
                    handleChange('theme', newTheme);
                  }}
                  className="setting-select"
                >
                  <option value="light">{t('userProfile.themeLight', 'Light')}</option>
                  <option value="dark">{t('userProfile.themeDark', 'Dark')}</option>
                  <option value="auto">{t('userProfile.themeAuto', 'Auto (System)')}</option>
                </select>
              </div>
            </div>

            <div className="setting-item setting-item-toggle">
              <div className="setting-label">
                <i className="fas fa-compress-alt"></i>
                <div>
                  <span className="setting-title">{t('userProfile.compactView', 'Compact View')}</span>
                  <span className="setting-description">{t('userProfile.compactViewDescription', 'Use condensed layouts')}</span>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.compactView}
                  onChange={(e) => handleChange('compactView', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="profile-tab-content">
            <div
              className="setting-item setting-item-clickable"
              onClick={() => {
                onClose();
                navigate('/account', { state: { activeTab: 'alerting' } });
              }}
            >
              <div className="setting-label">
                <i className="fas fa-bell"></i>
                <div>
                  <span className="setting-title">{t('userProfile.manageAlerts', 'Manage Alerts')}</span>
                  <span className="setting-description">{t('userProfile.manageAlertsDescription', 'Configure notification preferences')}</span>
                </div>
              </div>
              <i className="fas fa-chevron-right setting-arrow"></i>
            </div>

            <div className="setting-item setting-item-toggle">
              <div className="setting-label">
                <i className="fas fa-sync"></i>
                <div>
                  <span className="setting-title">{t('userProfile.autoRefresh', 'Auto-refresh')}</span>
                  <span className="setting-description">{t('userProfile.autoRefreshDescription', 'Automatically update data')}</span>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.autoRefresh}
                  onChange={(e) => handleChange('autoRefresh', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings.autoRefresh && (
              <div className="setting-item sub-setting">
                <div className="setting-label">
                  <i className="fas fa-clock"></i>
                  <div>
                    <span className="setting-title">{t('userProfile.refreshInterval', 'Refresh Interval')}</span>
                    <span className="setting-description">{t('userProfile.refreshIntervalDescription', 'Seconds between updates')}</span>
                  </div>
                </div>
                <div className="setting-control">
                  <input
                    type="number"
                    value={settings.refreshInterval}
                    onChange={(e) => handleChange('refreshInterval', Number(e.target.value))}
                    min="5"
                    max="300"
                    className="setting-input"
                  />
                </div>
              </div>
            )}

            <div className="setting-item">
              <div className="setting-label">
                <i className="fas fa-list-ol"></i>
                <div>
                  <span className="setting-title">{t('userProfile.resultsLimit', 'Results Limit')}</span>
                  <span className="setting-description">{t('userProfile.resultsLimitDescription', 'Switch to AJAX above this')}</span>
                </div>
              </div>
              <div className="setting-control">
                <input
                  type="number"
                  value={settings.scanResultsLimit}
                  onChange={(e) => handleChange('scanResultsLimit', Number(e.target.value))}
                  min="50"
                  max="5000"
                  step="50"
                  className="setting-input"
                />
              </div>
            </div>

            {/* Push Notifications Section */}
            <div className="setting-section-header" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              marginBottom: '0.25rem',
              paddingBottom: '0.375rem',
              borderBottom: '1px solid var(--color-border-primary)',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              <i className="fas fa-bell"></i>
              {t('userProfile.pushNotifications', 'Push Notifications')}
            </div>

            <div className="setting-item setting-item-toggle">
              <div className="setting-label">
                <i className="fas fa-mobile-alt"></i>
                <div>
                  <span className="setting-title">{t('userProfile.pushover', 'Pushover')}</span>
                  <span className="setting-description">{t('userProfile.pushoverDescription', 'Receive push notifications on your phone')}</span>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={pushoverEnabled}
                  onChange={(e) => setPushoverEnabled(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {pushoverEnabled && (
              <div className="setting-item sub-setting">
                <div className="setting-label">
                  <i className="fas fa-key"></i>
                  <div>
                    <span className="setting-title">{t('userProfile.pushoverUserKey', 'Pushover User Key')}</span>
                    <span className="setting-description">
                      <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary-500)', textDecoration: 'none' }}>
                        {t('userProfile.getKeyAtPushover', 'Get key at pushover.net')}
                      </a>
                    </span>
                  </div>
                </div>
                <div className="setting-control">
                  <input
                    type="password"
                    value={pushoverKey}
                    onChange={(e) => setPushoverKey(e.target.value)}
                    placeholder={t('userProfile.enterPushoverKey', 'Enter your Pushover user key')}
                    className="setting-input"
                    style={{ width: '180px' }}
                  />
                </div>
              </div>
            )}

            {pushoverEnabled && (
              <div style={{ marginTop: '0.25rem' }}>
                <button
                  className="save-btn"
                  onClick={savePushoverSettings}
                  disabled={pushSaving}
                  style={{
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.75rem',
                    backgroundColor: pushSaving ? 'var(--color-text-muted)' : 'var(--color-primary-500)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pushSaving ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  {pushSaving ? (
                    <><i className="fas fa-spinner fa-spin"></i> {t('common.saving', 'Saving...')}</>
                  ) : (
                    <><i className="fas fa-save"></i> {t('userProfile.savePushSettings', 'Save Push Settings')}</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="profile-tab-content">
            {/* Change Password Section */}
            <div className="security-section" style={{ marginBottom: '1.5rem' }}>
              <h4 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <i className="fas fa-key" style={{ color: 'var(--color-warning)' }}></i>
                {t('userProfile.changePassword', 'Change Password')}
              </h4>

              {passwordMessage && (
                <div style={{
                  backgroundColor: passwordMessage.type === 'success' ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
                  border: `1px solid ${passwordMessage.type === 'success' ? 'var(--color-tint-green)' : 'var(--color-tint-red)'}`,
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  marginBottom: '0.75rem',
                  color: passwordMessage.type === 'success' ? 'var(--color-success-dark)' : 'var(--color-critical)',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className={`fas ${passwordMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                  {passwordMessage.text}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <input
                  type="password"
                  placeholder={t('userProfile.currentPassword', 'Current Password')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border-primary)',
                    backgroundColor: 'var(--color-card-background)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.8125rem',
                    outline: 'none',
                  }}
                />
                <input
                  type="password"
                  placeholder={t('userProfile.newPassword', 'New Password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border-primary)',
                    backgroundColor: 'var(--color-card-background)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.8125rem',
                    outline: 'none',
                  }}
                />
                <input
                  type="password"
                  placeholder={t('userProfile.confirmNewPassword', 'Confirm New Password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border-primary)',
                    backgroundColor: 'var(--color-card-background)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.8125rem',
                    outline: 'none',
                  }}
                />
                {/* Real-time password requirements */}
                {newPassword && (() => {
                  const hasUpper = /[A-Z]/.test(newPassword);
                  const hasLower = /[a-z]/.test(newPassword);
                  const hasNumber = /[0-9]/.test(newPassword);
                  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
                  const meetsBase = hasUpper && hasLower && hasNumber;
                  const strength = !meetsBase || newPassword.length < 10
                    ? { label: t('userProfile.strengthWeak', 'Weak'), color: 'var(--color-error)', bg: 'var(--color-tint-red)' }
                    : newPassword.length >= 12 || (newPassword.length >= 10 && hasSpecial)
                      ? { label: t('userProfile.strengthGood', 'Good'), color: 'var(--color-success)', bg: 'var(--color-tint-green)' }
                      : { label: t('userProfile.strengthFair', 'Fair'), color: 'var(--color-warning)', bg: 'var(--color-tint-yellow)' };
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'var(--color-surface-elevated)',
                      borderRadius: '6px',
                      border: '1px solid var(--color-border-primary)',
                    }}>
                      {/* Strength indicator */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--color-text-tertiary)' }}>
                          {t('userProfile.requirements', 'Requirements')}
                        </span>
                        <span style={{
                          fontSize: '0.625rem',
                          fontWeight: '600',
                          color: strength.color,
                          backgroundColor: strength.bg,
                          padding: '0.125rem 0.5rem',
                          borderRadius: '9999px',
                          border: `1px solid ${strength.color}30`,
                        }}>
                          {strength.label}
                        </span>
                      </div>
                      {/* Strength bar */}
                      <div style={{ height: '3px', backgroundColor: 'var(--color-border-primary)', borderRadius: '2px', marginBottom: '0.25rem' }}>
                        <div style={{
                          height: '100%',
                          borderRadius: '2px',
                          backgroundColor: strength.color,
                          width: strength.label === t('userProfile.strengthGood', 'Good') ? '100%'
                            : strength.label === t('userProfile.strengthFair', 'Fair') ? '66%' : '33%',
                          transition: 'width 0.3s, background-color 0.3s',
                        }} />
                      </div>
                      {[
                        { met: newPassword.length >= 10, label: t('userProfile.reqMinLength', 'At least 10 characters') },
                        { met: hasUpper, label: t('userProfile.reqUppercase', 'One uppercase letter') },
                        { met: hasLower, label: t('userProfile.reqLowercase', 'One lowercase letter') },
                        { met: hasNumber, label: t('userProfile.reqNumber', 'One number') },
                        { met: confirmPassword ? newPassword === confirmPassword : true, label: t('userProfile.reqMatch', 'Passwords match'), hidden: !confirmPassword },
                      ].filter(r => !r.hidden).map((req, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.6875rem' }}>
                          <i className={`fas ${req.met ? 'fa-check-circle' : 'fa-circle'}`}
                             style={{ color: req.met ? 'var(--color-success)' : 'var(--color-border-secondary)', fontSize: '0.625rem' }}></i>
                          <span style={{ color: req.met ? 'var(--color-success-dark)' : 'var(--color-text-muted)' }}>{req.label}</span>
                        </div>
                      ))}
                      {meetsBase && newPassword.length >= 10 && newPassword.length < 12 && (
                        <div style={{ fontSize: '0.625rem', color: 'var(--color-warning)', marginTop: '0.125rem' }}>
                          <i className="fas fa-info-circle" style={{ marginRight: '0.25rem' }}></i>
                          {t('userProfile.strengthHint', 'Use 12+ characters for a strong password')}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <button
                  onClick={handleChangePassword}
                  disabled={passwordChanging || !currentPassword || !newPassword || !confirmPassword
                    || newPassword.length < 10 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)
                    || !/[0-9]/.test(newPassword) || newPassword !== confirmPassword}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: passwordChanging ? 'var(--color-text-muted)' : 'var(--color-warning)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: passwordChanging ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.8125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    opacity: (!currentPassword || !newPassword || !confirmPassword
                      || newPassword.length < 10 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)
                      || !/[0-9]/.test(newPassword) || newPassword !== confirmPassword) ? 0.6 : 1,
                  }}
                >
                  {passwordChanging ? (
                    <><i className="fas fa-spinner fa-spin"></i> {t('common.saving', 'Saving...')}</>
                  ) : (
                    <><i className="fas fa-save"></i> {t('userProfile.updatePassword', 'Update Password')}</>
                  )}
                </button>
              </div>
            </div>

            {/* Two-Factor Authentication Section */}
            <div className="security-section">
              <h4 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <i className="fas fa-mobile-alt" style={{ color: 'var(--color-primary-500)' }}></i>
                {t('userProfile.twoFactorAuth', 'Two-Factor Authentication')}
              </h4>

              {mfaMessage && (
                <div style={{
                  backgroundColor: 'var(--color-tint-green)',
                  border: '1px solid var(--color-tint-green)',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  marginBottom: '0.75rem',
                  color: 'var(--color-success-dark)',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="fas fa-check-circle"></i>
                  {mfaMessage}
                </div>
              )}

              {mfaLoading ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem',
                  color: 'var(--color-text-tertiary)'
                }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                  {t('common.loading', 'Loading...')}
                </div>
              ) : mfaStatus ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: mfaStatus.totp_enabled
                    ? 'var(--color-tint-green)'
                    : 'var(--color-surface-tertiary)',
                  borderRadius: '8px',
                  border: `1px solid ${mfaStatus.totp_enabled ? 'var(--color-success)' : 'var(--color-border-primary)'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: mfaStatus.totp_enabled ? 'var(--color-success)' : 'var(--color-text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className={`fas ${mfaStatus.totp_enabled ? 'fa-lock' : 'fa-lock-open'}`}
                         style={{ color: 'var(--color-text-inverse)', fontSize: '0.875rem' }}></i>
                    </div>
                    <div>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '0.8125rem',
                        color: 'var(--color-text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}>
                        {mfaStatus.totp_enabled
                          ? t('userProfile.mfaEnabled', 'Enabled')
                          : t('userProfile.mfaDisabled', 'Disabled')}
                        {mfaStatus.mfa_required && (
                          <span style={{
                            fontSize: '0.625rem',
                            backgroundColor: 'var(--color-purple-text)',
                            color: 'var(--color-text-inverse)',
                            padding: '0.0625rem 0.375rem',
                            borderRadius: '9999px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {t('userProfile.required', 'Required')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-tertiary)' }}>
                        {mfaStatus.totp_enabled
                          ? t('userProfile.mfaProtected', 'Your account is protected')
                          : mfaStatus.mfa_required
                            ? t('userProfile.mfaSetupRequired', 'Setup required by organization')
                            : t('userProfile.mfaRecommended', 'Recommended for security')}
                      </div>
                    </div>
                  </div>
                  {!mfaStatus.totp_enabled && (
                    <button
                      onClick={() => setShowMFASetup(true)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: 'var(--color-primary-500)',
                        color: 'var(--color-text-inverse)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}
                    >
                      <i className="fas fa-plus"></i>
                      {t('userProfile.setupMFA', 'Setup')}
                    </button>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: 'var(--color-text-tertiary)',
                  fontSize: '0.75rem'
                }}>
                  {t('userProfile.mfaLoadFailed', 'Unable to load MFA status')}
                  <button
                    onClick={loadMFAStatus}
                    style={{
                      display: 'block',
                      margin: '0.5rem auto 0',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'var(--color-primary-500)',
                      color: 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.6875rem'
                    }}
                  >
                    {t('common.retry', 'Retry')}
                  </button>
                </div>
              )}

              {/* Info about MFA */}
              <p style={{
                margin: '0.75rem 0 0 0',
                fontSize: '0.6875rem',
                color: 'var(--color-text-muted)',
                lineHeight: '1.4'
              }}>
                {t('userProfile.mfaInfo', 'Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app.')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* MFA Setup Modal */}
      {showMFASetup && (
        <MFASetup
          onComplete={handleMFASetupComplete}
          onCancel={() => setShowMFASetup(false)}
        />
      )}

      {/* Footer */}
      <div className="profile-dropdown-footer">
        {(activeTab === 'appearance' || activeTab === 'preferences') && (
          <button
            className="save-btn"
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? (
              <><i className="fas fa-spinner fa-spin"></i> {t('common.saving', 'Saving...')}</>
            ) : message ? (
              <><i className="fas fa-check"></i> {t('userProfile.settingsSaved', 'Settings saved')}</>
            ) : (
              <><i className="fas fa-save"></i> {t('common.saveChanges', 'Save Changes')}</>
            )}
          </button>
        )}
        <button className="logout-dropdown-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i>
          {t('common.signOut', 'Sign Out')}
        </button>
      </div>
    </div>
  );
};

export default UserProfileDropdown;
